import Button from '@jetbrains/ring-ui/components/button/button'
import ButtonGroup from '@jetbrains/ring-ui/components/button-group/button-group'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import {skipToken} from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import {memo, useCallback, useState} from 'react'

import {PlaceIdList} from '../../../../plugins/plugins.types'
import {getBuild} from '../../../../selectors'
import {restApi} from '../../../../services/rest'
import Plugin from '../../../common/Plugin/Plugin'
import SequenceLoader from '../../../common/SequenceLoader/SequenceLoader'
import {getTestOccurenceArg} from '../Tests.rest'
import {
  getBaseTestOccurrence,
  getTestOccurrenceFirstFailed,
  getTestOccurrenceNewFailure,
  getTestOccurrenceNextFixed,
} from '../Tests.selectors'
import {TestStatus} from '../Tests.types'

import BuildTestTab from './BuildTestTab/BuildTestTab.container'
import type {Props} from './TestDetails.types'
import {TestTab} from './TestDetails.types'

import styles from './TestDetails.css'

import {useAppSelector} from 'src/hooks/react-redux'

function TestDetails({className, buildId, testOccurrenceId}: Props) {
  restApi.endpoints.getTestOccurrenceNormalized.useQuerySubscription(
    testOccurrenceId != null
      ? getTestOccurenceArg(testOccurrenceId, {
          withNewFailure: true,
          withMetadataCount: true,
          withNextFixed: true,
          withFirstFailed: true,
          withMuteInfo: true,
          withInvestigationInfo: true,
        })
      : skipToken,
  )
  const composite = useAppSelector(state => getBuild(state, buildId)?.composite)
  const id = useAppSelector(state => getBaseTestOccurrence(state, testOccurrenceId)?.id)
  const status = useAppSelector(state => getBaseTestOccurrence(state, testOccurrenceId)?.status)
  const logAnchor = useAppSelector(
    state => getBaseTestOccurrence(state, testOccurrenceId)?.logAnchor,
  )
  const newFailure = useAppSelector(state => getTestOccurrenceNewFailure(state, testOccurrenceId))
  const firstFailed = useAppSelector(state => getTestOccurrenceFirstFailed(state, testOccurrenceId))
  const nextFixed = useAppSelector(state => getTestOccurrenceNextFixed(state, testOccurrenceId))
  const [selectedTab, setTab] = useState<TestTab>(TestTab.CURRENT_FAILURE)
  const selectCurrentFailureTab = useCallback(() => setTab(TestTab.CURRENT_FAILURE), [setTab])
  const selectFirstFailureTab = useCallback(() => setTab(TestTab.FIRST_FAILURE), [setTab])
  const selectNextFixedTab = useCallback(() => setTab(TestTab.NEXT_FIXED), [setTab])
  let selectedTestOccurrenceId = id

  if (selectedTab === TestTab.FIRST_FAILURE) {
    selectedTestOccurrenceId = firstFailed?.id
  }

  if (selectedTab === TestTab.NEXT_FIXED) {
    selectedTestOccurrenceId = nextFixed?.id
  }

  const Loader = <LoaderInline className={styles.loader}>{'Loading test details...'}</LoaderInline>
  return (
    <div className={classNames(className, styles.wrapper)}>
      {id != null ? (
        <>
          {status === TestStatus.FAILURE && (nextFixed != null || newFailure === false) && (
            <ButtonGroup className={styles.buttons}>
              <Button
                onClick={selectCurrentFailureTab}
                active={selectedTab === TestTab.CURRENT_FAILURE}
              >
                {newFailure === true ? 'First failure' : 'Current failure'}
              </Button>
              {newFailure === false && (
                <Button
                  onClick={selectFirstFailureTab}
                  active={selectedTab === TestTab.FIRST_FAILURE}
                >
                  {'First failure'}
                </Button>
              )}
              {nextFixed != null && (
                <Button onClick={selectNextFixedTab} active={selectedTab === TestTab.NEXT_FIXED}>
                  {'Already fixed'}
                </Button>
              )}
            </ButtonGroup>
          )}
          <Plugin
            placeId={PlaceIdList.SAKURA_TEST_DETAILS_ACTIONS}
            buildId={buildId}
            testRunId={logAnchor}
          />
          {selectedTestOccurrenceId != null && (
            <SequenceLoader loader={Loader}>
              <BuildTestTab
                buildId={buildId}
                seqKey="BuildTestTab"
                testOccurrenceId={selectedTestOccurrenceId}
                withBuildInfo={composite === true || nextFixed != null || newFailure === false}
              />
            </SequenceLoader>
          )}
        </>
      ) : (
        Loader
      )}
    </div>
  )
}

export default memo<Props>(TestDetails)
